// Floating Team Chat Component - Shows only on authenticated client portal pages
import React, { useState, useEffect } from 'react';
import { MessageCircle } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import TeamChatModal from './TeamChatModal';
import { useAIDrawer } from '../../contexts/AIDrawerContext';
import socketService from '../../services/socketService';

const FloatingTeamChat = ({ currentUser }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { isAIDrawerOpen } = useAIDrawer();
  const location = useLocation();

  console.log('💬 FloatingTeamChat Rendered!', { currentUser, isOpen });

  // Listen for new messages to update unread count
  useEffect(() => {
    if (!currentUser) return;

    const handleNewMessage = (message) => {
      // Only increment if chat is closed and message is not from current user
      if (!isOpen && message.userId !== currentUser.id) {
        setUnreadCount(prev => prev + 1);
      }
    };

    socketService.on('message:new', handleNewMessage);

    return () => {
      socketService.off('message:new', handleNewMessage);
    };
  }, [isOpen, currentUser]);

  // Reset unread count when chat is opened
  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  // Define pages where chat should NOT appear (public marketing pages and admin/settings pages)
  const excludedPaths = [
    // Public marketing pages
    '/ai-agents',
    '/features',
    '/pricing',
    '/about',
    '/contact',
    '/blog',
    '/login',
    '/signup',
    '/enterprise',
    // Admin/settings pages
    '/account',
    '/account-settings',
    '/billing',
    '/notifications',
    '/settings',
    '/profile',
    '/preferences',
    '/subscription',
    '/payment'
  ];

  // Check if current path should hide the chat bubble
  const shouldHideChat = excludedPaths.some(path => 
    location.pathname.toLowerCase().includes(path.toLowerCase())
  );

  // Hide chat bubble on admin/settings pages
  if (shouldHideChat && !isOpen) {
    return null;
  }

  // Don't show button when chat modal is open
  if (isOpen) {
    return (
      <TeamChatModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        currentUser={currentUser}
      />
    );
  }

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="team-chat-bubble-button"
        style={{ 
          position: 'fixed',
          bottom: '1.5rem',
          right: '1.5rem',
          zIndex: 9999 
        }}
      >
        <img 
          src="/plugly-logo.png" 
          alt="Plugly AI" 
          className="chat-bubble-logo"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'block';
          }}
        />
        <MessageCircle className="w-7 h-7 chat-bubble-icon" style={{ display: 'none' }} />
        
        {/* Tooltip */}
        <span className="tooltip">Team Chat</span>
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse shadow-lg">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      <style>{`
        .team-chat-bubble-button {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          border: none;
          background-color: #14b8a6;
          background-image: linear-gradient(147deg, #5eead4 0%, #14b8a6 74%);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0px 10px 10px rgba(0, 0, 0, 0.151);
          position: relative;
        }

        .chat-bubble-logo {
          width: 1.5rem;
          height: 1.5rem;
          object-fit: contain;
          filter: brightness(0) invert(1);
        }

        .chat-bubble-icon {
          fill: white;
        }

        .team-chat-bubble-button:hover .chat-bubble-logo,
        .team-chat-bubble-button:hover .chat-bubble-icon {
          animation: jello-vertical 0.7s both;
        }

        @keyframes jello-vertical {
          0% {
            transform: scale3d(1, 1, 1);
          }
          30% {
            transform: scale3d(0.75, 1.25, 1);
          }
          40% {
            transform: scale3d(1.25, 0.75, 1);
          }
          50% {
            transform: scale3d(0.85, 1.15, 1);
          }
          65% {
            transform: scale3d(1.05, 0.95, 1);
          }
          75% {
            transform: scale3d(0.95, 1.05, 1);
          }
          100% {
            transform: scale3d(1, 1, 1);
          }
        }

        .tooltip {
          position: absolute;
          top: -20px;
          opacity: 0;
          background-color: #14b8a6;
          background-image: linear-gradient(147deg, #5eead4 0%, #14b8a6 74%);
          color: white;
          padding: 5px 10px;
          border-radius: 5px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition-duration: 0.2s;
          pointer-events: none;
          letter-spacing: 0.5px;
          font-size: 12px;
          font-weight: 500;
          white-space: nowrap;
        }

        .tooltip::before {
          position: absolute;
          content: "";
          width: 10px;
          height: 10px;
          background-color: #14b8a6;
          background-size: 1000%;
          background-position: center;
          transform: rotate(45deg);
          bottom: -15%;
          transition-duration: 0.3s;
        }

        .team-chat-bubble-button:hover .tooltip {
          top: -40px;
          opacity: 1;
          transition-duration: 0.3s;
        }
      `}</style>
    </>
  );
};

export default FloatingTeamChat;
